import React, { useState, useEffect } from "react";
import { Row, Col, Input, Button, Breadcrumb, message, Spin, Empty, Modal } from "antd";
import { SearchOutlined, UploadOutlined, HomeOutlined } from "@ant-design/icons";
import axios from "axios";

import "./Documents.css";
import FolderCard from "./folders/FolderCard";
import DocumentCard from "./DocumentCard";
import UploadDocumentModal from "./UploadDocument";
import UpdateFileModal from "./UpdateFileModal";
import ShareFileModal from "./ShareModal";
import AddFolderModal from "./folders/AddFolderModal";
import { isAuthenticated } from "../../commons/Auth/auth";

import { useTranslation } from "react-i18next";
import PageToolbar from "../../design-system/PageToolbar";
import { saveFile } from "../../native/files";

const CommonDocumentsManagement = () => {
  const { t } = useTranslation("dashboard");
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [editDocumentModal, setEditDocumentModal] = useState(false);
  const [shareDocumentModal, setShareDocumentModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [sharingResource, setSharingResource] = useState(null);
  const [sharingType, setSharingType] = useState("document");
  const [allFolders, setAllFolders] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchContent = async (folderId = null) => {
    setLoading(true);
    try {
      if (folderId) {
        const folderResponse = await axios.get(`${API_URL}/folders/${folderId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        });

        if (folderResponse.data.data) {
          setDocuments(folderResponse.data.data.documents || []);
          setFolders(folderResponse.data.data.subfolders || []);

          const breadcrumbResponse = await axios.get(`${API_URL}/folders/breadcrumb/${folderId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          });
          setBreadcrumb(breadcrumbResponse.data.data || []);
        }
      } else {
        const docResponse = await axios.post(
          `${API_URL}/documents/list`,
          {
            folderId: null,
            searchText: searchText,
            pageSize: 50,
            pageNo: 0
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        );

        const folderResponse = await axios.post(
          `${API_URL}/folders/list`,
          {
            parentFolder: null,
            searchText: searchText,
            pageSize: 50,
            pageNo: 0
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        );

        setDocuments(docResponse.data.data || []);
        setFolders(folderResponse.data.data || []);
        setBreadcrumb([]);
      }
    } catch (error) {
      console.error(error);
      message.error(t("Failed to fetch content"));
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFolders = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/folders/list`,
        {
          pageSize: 100,
          pageNo: 0
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      setAllFolders(response.data.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchContent(currentFolder);
  }, [currentFolder, searchText]);

  useEffect(() => {
    fetchAllFolders();
  }, []);

  const refreshAll = () => {
    fetchContent(currentFolder);
    fetchAllFolders();
  };

  const navigateToFolder = folderId => {
    setCurrentFolder(folderId);
  };

  const navigateToHome = () => {
    setCurrentFolder(null);
  };

  const handleDeleteDocument = async documentId => {
    Modal.confirm({
      title: t("Delete Document"),
      content: t("Are you sure you want to delete this document?"),
      okType: "danger",
      onOk: async () => {
        try {
          await axios.delete(`${API_URL}/documents/delete/${documentId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          });
          message.success(t("Document deleted successfully"));
          fetchContent(currentFolder);
        } catch (error) {
          message.error(t("Failed to delete document"));
        }
      }
    });
  };

  const handleDeleteFolder = async folderId => {
    Modal.confirm({
      title: t("Delete Folder"),
      content: t("Are you sure you want to delete this folder? It must be empty."),
      okType: "danger",
      onOk: async () => {
        try {
          await axios.delete(`${API_URL}/folders/delete/${folderId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          });
          message.success(t("Folder deleted successfully"));
          refreshAll();
        } catch (error) {
          message.error(t("Failed to delete folder"));
        }
      }
    });
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await axios.get(`${API_URL}/documents/download/${documentId}`, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });

      await saveFile(new Blob([response.data]), filename);
    } catch (error) {
      message.error(t("Failed to download document"));
    }
  };

  const openEditModal = document => {
    setEditingDocument(document);
    setEditDocumentModal(true);
  };

  const openShareModal = (resource, type = "document") => {
    setSharingResource(resource);
    setSharingType(type);
    setShareDocumentModal(true);
  };

  const crumbButton = (label, onClick, icon) => (
    <button type="button" className="documents-page__crumb" onClick={onClick}>
      {icon}
      {label}
    </button>
  );

  return (
    <div className="documents-page">
      <h2 className="documents-page__title">{t("File Manager")}</h2>
      <PageToolbar
        filters={
          <Input
            type="search"
            enterKeyHint="search"
            allowClear
            placeholder={t("Search everything...")}
            aria-label={t("Search everything...")}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        }
        actions={
          <>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              {t("Upload")}
            </Button>
            <AddFolderModal
              updateParent={refreshAll}
              parentFolderId={currentFolder}
              folders={allFolders}
            />
          </>
        }
      />

      <div className="documents-page__breadcrumb">
        <Breadcrumb
          items={[
            { key: "home", title: crumbButton(t("Home"), navigateToHome, <HomeOutlined />) },
            ...breadcrumb.map(item => ({
              key: item._id,
              title: crumbButton(item.name, () => navigateToFolder(item._id))
            }))
          ]}
        />
      </div>

      <Spin spinning={loading}>
        {folders.length > 0 && (
          <Row gutter={[16, 16]} className="documents-page__grid">
            {folders.map(folder => (
              <Col key={folder._id} xs={12} sm={8} md={6} xl={4}>
                <FolderCard
                  folder={folder}
                  onOpen={() => navigateToFolder(folder._id)}
                  onShare={() => openShareModal(folder, "folder")}
                  onDelete={() => handleDeleteFolder(folder._id)}
                />
              </Col>
            ))}
          </Row>
        )}

        {documents.length > 0 ? (
          <Row gutter={[16, 16]} className="documents-page__grid">
            {documents.map(document => (
              <Col key={document._id} xs={12} sm={8} md={6} xl={4}>
                <DocumentCard
                  document={document}
                  onDownload={() => handleDownloadDocument(document._id, document.originalName)}
                  onEdit={() => openEditModal(document)}
                  onShare={() => openShareModal(document)}
                  onDelete={() => handleDeleteDocument(document._id)}
                />
              </Col>
            ))}
          </Row>
        ) : (
          !loading && (
            <Empty
              className="documents-page__empty"
              description={searchText ? t("No results found") : t("No documents in this folder")}
            />
          )
        )}
      </Spin>

      <UploadDocumentModal
        visible={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        onSuccess={() => {
          setUploadModalVisible(false);
          fetchContent(currentFolder);
        }}
        currentFolder={currentFolder}
        allFolders={allFolders}
        apiUrl={API_URL}
      />

      <UpdateFileModal
        visible={editDocumentModal}
        document={editingDocument}
        onCancel={() => {
          setEditDocumentModal(false);
          setEditingDocument(null);
        }}
        onSuccess={() => {
          setEditDocumentModal(false);
          setEditingDocument(null);
          fetchContent(currentFolder);
        }}
        allFolders={allFolders}
        apiUrl={API_URL}
      />

      <ShareFileModal
        visible={shareDocumentModal}
        updateParent={() => fetchContent(currentFolder)}
        resource={sharingResource}
        type={sharingType}
        onCancel={() => {
          setShareDocumentModal(false);
          setSharingResource(null);
        }}
        apiUrl={API_URL}
      />
    </div>
  );
};

export default CommonDocumentsManagement;
